import {
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TranslateService } from '../services/i18n/translate.service';
import {
  getErrorType,
  getErrorTranslationKey,
  shouldHandleError,
} from '../utils/error-mapper.util';
import {
  shouldUseTransferTokenErrorMessage,
  TRANSFER_TOKEN_ERROR_MESSAGE_KEY,
} from '../utils/transfer-token-error.util';
import { SUPPRESS_GLOBAL_ERROR_TOAST } from './suppress-error-toast.context';

const SILENT_404_PATHS = [
  'payment/reminder/notification/me',
  'payment/reminder/adhesions/me',
];

/** Rutas donde no mostramos toast global para 5xx/red: el servicio reintenta y las pantallas hacen fallback. */
const SUPPRESS_ERROR_TOAST_PATHS_TRANSIENT = ['business/user'];

function isExpectedSilent404(error: HttpErrorResponse): boolean {
  if (error.status !== 404) {
    return false;
  }

  const url = (error.url || '').toLowerCase();
  return SILENT_404_PATHS.some((path) => url.includes(path));
}

/**
 * GET /api/Business/user: ante 500 el backend puede responder errores transitorios; el cliente ya reintenta.
 * Si mostráramos el toast aquí, saldría una vez por intento. Las vistas tratan roles vacíos con catchError.
 */
function isSuppressGlobalToastTransientRoles(error: HttpErrorResponse): boolean {
  const url = (error.url || '').toLowerCase();
  if (!SUPPRESS_ERROR_TOAST_PATHS_TRANSIENT.some((path) => url.includes(path))) {
    return false;
  }
  const status = error.status;
  return status === 0 || (status >= 500 && status < 600);
}

/**
 * Interceptor centralizado para manejo de errores HTTP.
 * Convierte errores del backend en mensajes amigables para el usuario.
 */
export const errorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const translateService = inject(TranslateService);
  const messageService = inject(MessageService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      if (isExpectedSilent404(error)) {
        return throwError(() => error);
      }

      if (isSuppressGlobalToastTransientRoles(error)) {
        return throwError(() => error);
      }

      if (!shouldHandleError(error)) {
        return throwError(() => error);
      }

      if (req.context.get(SUPPRESS_GLOBAL_ERROR_TOAST)) {
        return throwError(() => error);
      }

      const errorTitle = translateService.translate('common.error.title');
      const errorType = getErrorType(error);
      const translationKey = shouldUseTransferTokenErrorMessage(error, req.url)
        ? TRANSFER_TOKEN_ERROR_MESSAGE_KEY
        : getErrorTranslationKey(errorType);
      const userMessage = translateService.translate(translationKey);

      if (errorTitle !== 'common.error.title' && userMessage !== translationKey) {
        messageService.add({
          severity: 'error',
          summary: errorTitle,
          detail: userMessage,
          life: 5000,
        });
      } else {
        messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Ocurrió un error. Por favor, intenta nuevamente.',
          life: 5000,
        });
      }

      return throwError(() => {
        const transformedError = new HttpErrorResponse({
          error: {
            ...error.error,
            userMessage,
            errorType,
          },
          headers: error.headers,
          status: error.status,
          url: error.url ?? undefined, // Convertir null a undefined
        });
        return transformedError;
      });
    })
  );
};
